import { differenceInCalendarDays, isSameDay, startOfDay } from 'date-fns'
import { getResourceGlyph, type Theme } from '@opallabs/gem'
import { compareChips } from '../laneLayout'
import { parseCalendarDate } from '../monthGrid'
import {
  type CalendarItem,
  type CalendarItemKind,
  type ResolveDisplay,
} from '../types'

// The marks the rest of the product draws for these kinds, and the ones the
// grid's own chips and bars use.
const KIND_GLYPHS: Partial<Record<CalendarItemKind, string>> = {
  content: 'gem-content-outlined',
  moment: 'gem-moment-outlined',
}

/**
 * The mark a panel draws beside a name.
 *
 * `getResourceGlyph` answers with the filled resource tile — a solid square in
 * one fixed colour — which belongs in a picker of object types and reads as a
 * blob beside a title. Content and moments have a line mark of their own, and
 * that is the one the grid uses.
 */
export const calendarKindGlyph = (
  theme: Theme,
  kind: CalendarItemKind
): string => KIND_GLYPHS[kind] ?? getResourceGlyph(theme, kind)

/** One row of the day panel's "Ongoing" list. */
export type CalendarOngoingEntry = {
  end: Date
  /** True when the row is about the item's in-market window, not its schedule. */
  isInMarket: boolean
  item: CalendarItem
  key: string
  start: Date
}

export type BuildOngoingEntriesOptions = {
  /** @default true */
  shouldShowInMarketBars?: boolean
}

/**
 * The runs of days that pass through a date: anything scheduled across more than
 * one day, plus content that is live on its channel that day.
 *
 * An item gets at most one row, and its schedule wins over its in-market window,
 * so a piece of content that runs for a week is not listed twice.
 */
export const buildOngoingEntries = (
  items: readonly CalendarItem[],
  date: Date,
  { shouldShowInMarketBars = true }: BuildOngoingEntriesOptions = {}
): CalendarOngoingEntry[] => {
  const day = startOfDay(date)
  const entries: CalendarOngoingEntry[] = []

  items.forEach((item) => {
    const start = parseCalendarDate(item.start, { field: 'start', id: item.id })
    const end = parseCalendarDate(item.end, { field: 'end', id: item.id })

    if (
      start !== null &&
      end !== null &&
      differenceInCalendarDays(end, start) > 0
    ) {
      if (start <= day && end >= day) {
        entries.push({ end, isInMarket: false, item, key: item.id, start })
      }
      return
    }

    if (!shouldShowInMarketBars || !item.inMarket) return

    const inMarketStart = parseCalendarDate(item.inMarket.start, {
      field: 'inMarket.start',
      id: item.id,
    })
    const inMarketEnd = parseCalendarDate(item.inMarket.end, {
      field: 'inMarket.end',
      id: item.id,
    })
    if (inMarketStart === null || inMarketEnd === null) return
    if (inMarketStart > day || inMarketEnd < day) return

    entries.push({
      end: inMarketEnd,
      isInMarket: true,
      item,
      key: `${item.id}:in-market`,
      start: inMarketStart,
    })
  })

  // Moments first, as the day's own list has them. Then earliest first, then
  // by key, so a re-fetch that returns the items in a different order cannot
  // reshuffle the panel under a person's cursor.
  return entries.sort(
    (a, b) =>
      (a.item.kind === 'moment' ? 0 : 1) - (b.item.kind === 'moment' ? 0 : 1) ||
      a.start.getTime() - b.start.getTime() ||
      a.key.localeCompare(b.key)
  )
}

const compareDayItems = (a: CalendarItem, b: CalendarItem): number =>
  (a.kind === 'moment' ? 0 : 1) - (b.kind === 'moment' ? 0 : 1) ||
  compareChips(a, b)

/**
 * What a day panel lists below "Ongoing": the moments and content that belong to
 * that one day. Runs of days are left out because "Ongoing" already has them.
 */
export const buildDayItems = (
  items: readonly CalendarItem[],
  date: Date
): CalendarItem[] => {
  const day = startOfDay(date)

  return items
    .filter((item) => {
      if (item.kind !== 'content' && item.kind !== 'moment') return false

      const start = parseCalendarDate(item.start, {
        field: 'start',
        id: item.id,
      })
      const end = parseCalendarDate(item.end, { field: 'end', id: item.id })

      return (
        start !== null &&
        end !== null &&
        isSameDay(start, end) &&
        isSameDay(start, day)
      )
    })
    .sort(compareDayItems)
}

/**
 * Everything the grid draws, in the order a person reads it: day by day, each
 * day's moments before its content, then the way a day lists them. A run of
 * days is placed on the day it starts, which is where the grid starts drawing
 * it. This is what the item panel's previous and next walk, so a person can
 * move through the whole month from the panel, wrapping at either end.
 */
export const buildGridOrder = (
  items: readonly CalendarItem[]
): CalendarItem[] =>
  items
    .filter((item) => item.kind === 'content' || item.kind === 'moment')
    // ISO dates at day precision order as text the way they do as dates.
    .sort((a, b) => a.start.localeCompare(b.start) || compareDayItems(a, b))

/** How many channels a day summary names before it counts the rest. */
export const DAY_SUMMARY_CHANNEL_CAP = 4

/** One channel's share of a day's content, for the day panel's summary. */
export type CalendarChannelShare = {
  count: number
  id: string
  /** Something on the channel, so the caller can draw the channel's own mark. */
  item: CalendarItem
  name: string
  /** Whole percent of the day's content that could be put on a channel. */
  share: number
}

/** What a day holds, in one line and a short list of channels. */
export type CalendarDaySummary = {
  /** The busiest channels, at most `DAY_SUMMARY_CHANNEL_CAP` of them. */
  channels: CalendarChannelShare[]
  contentCount: number
  /** Channels the cap left out. */
  hiddenChannelCount: number
  momentCount: number
}

/**
 * The summary at the top of a day panel, counted over exactly the items the
 * panel lists — so the line and the rows below it can never disagree.
 *
 * Shares are of the content that could be put on a named channel, not of all
 * the content, which is why four channels on nine of twelve items still add up
 * to a hundred.
 */
export const buildDaySummary = (
  items: readonly CalendarItem[],
  {
    channelCap = DAY_SUMMARY_CHANNEL_CAP,
    resolveDisplay,
  }: { channelCap?: number; resolveDisplay?: ResolveDisplay }
): CalendarDaySummary => {
  const buckets = new Map<string, CalendarChannelShare>()
  let momentCount = 0
  let contentCount = 0

  items.forEach((item) => {
    if (item.kind === 'moment') momentCount += 1
    if (item.kind !== 'content') return
    contentCount += 1

    if (item.channelId === undefined) return
    const found = buckets.get(item.channelId)
    if (found !== undefined) {
      found.count += 1
      return
    }

    // A channel nobody can name is left out rather than shown as an id, and it
    // is left out of the shares as well.
    const name = resolveDisplay?.('channel', item.channelId)
    if (name === undefined) return

    buckets.set(item.channelId, {
      count: 1,
      id: item.channelId,
      item,
      name,
      share: 0,
    })
  })

  const channels = Array.from(buckets.values()).sort(
    (a, b) => b.count - a.count || a.name.localeCompare(b.name)
  )
  const channelled = channels.reduce((sum, channel) => sum + channel.count, 0)
  channels.forEach((channel) => {
    channel.share = Math.round((channel.count / channelled) * 100)
  })

  return {
    channels: channels.slice(0, channelCap),
    contentCount,
    hiddenChannelCount: Math.max(channels.length - channelCap, 0),
    momentCount,
  }
}

/**
 * The id `step` places along from `currentId`, wrapping round at both ends.
 *
 * `null` when there is nowhere to go, which is what turns the panel's previous
 * and next buttons off rather than letting them do nothing.
 */
export const stepCycle = (
  ids: readonly string[],
  currentId: string,
  step: number
): string | null => {
  const index = ids.indexOf(currentId)
  if (index === -1 || ids.length < 2) return null

  return ids[(((index + step) % ids.length) + ids.length) % ids.length]
}

/**
 * The content of one moment that is in view, in the order a day lists it.
 *
 * In view is the whole of it: the calendar loads a month, so a moment that runs
 * past the edge of it has content this cannot see. The panel says as much by
 * listing what it has rather than by claiming a total.
 */
export const buildMomentContent = (
  items: readonly CalendarItem[],
  momentId: string
): CalendarItem[] =>
  items
    .filter((item) => item.kind === 'content' && item.parentId === momentId)
    .sort(compareChips)

/** The dates the item panel leads with, as ISO dates at day precision. */
export type CalendarItemDates = {
  /**
   * The day it comes down: a moment's last day, or content's takedown. Absent
   * when nothing takes the content down.
   */
  end?: string
  /** The day it goes up. Absent only when the date cannot be read. */
  start?: string
  /** The time of day it goes up, with the zone it is read in: "9:00 AM PDT". */
  time?: string
}

export type BuildItemDatesParams = {
  /**
   * Names the zone an instant is read in: `PDT`. The month endpoint carries no
   * zone of its own, so this is the viewer's — the same zone the time itself
   * was formatted in.
   */
  formatTimeZone?: (isoInstant: string) => string | undefined
  item: CalendarItem
}

/**
 * When an item goes up and when it comes down.
 *
 * Content is always scheduled on a single day, so its end is its takedown; a
 * moment runs for as long as its own dates say, and a one-day moment ends on
 * the day it starts. A date that cannot be read is left out: the grid has
 * already logged it, and a date field with nothing in it says "None", which
 * tells a person more than "Invalid Date".
 */
export const buildItemDates = ({
  formatTimeZone,
  item,
}: BuildItemDatesParams): CalendarItemDates => {
  const readable = (isoDate: string | undefined, field: string) =>
    isoDate !== undefined &&
    parseCalendarDate(isoDate, { field, id: item.id }) !== null
      ? isoDate
      : undefined

  const zone =
    item.time === undefined || item.startsAt === undefined
      ? undefined
      : formatTimeZone?.(item.startsAt)
  const time = zone === undefined ? item.time : `${item.time} ${zone}`

  const end = item.kind === 'moment' ? item.end : item.inMarket?.end

  return {
    end: readable(end, 'end'),
    start: readable(item.start, 'start'),
    time,
  }
}

/** The mark that leads a field's value. The panel owns the drawing. */
export type CalendarFieldMark =
  | 'account'
  | 'channel'
  | 'moment'
  | 'story'
  | 'workflow'

export type CalendarItemField = {
  /** The id the item carries, which is what a select would hold as its value. */
  id: string
  label: string
  mark?: CalendarFieldMark
  /** The id, named. */
  value: string
}

export type BuildItemFieldsParams = {
  item: CalendarItem
  /** Everything currently in view, used to name a parent moment without a lookup. */
  itemsById: Map<string, CalendarItem>
  resolveDisplay?: ResolveDisplay
}

/**
 * The item panel's field rows, in the order the design lists them.
 *
 * A field appears only when the item carries it *and* the value can be read as
 * text. Relationships arrive as ids, so a panel with no resolver shows the
 * dates and nothing else — which is honest, where printing an id would not be.
 *
 * Privacy and status are not here: the lock beside "Open" says whether the item
 * is private, and Workflow says what state it is in.
 */
export const buildItemFields = ({
  item,
  itemsById,
  resolveDisplay,
}: BuildItemFieldsParams): CalendarItemField[] => {
  const fields: CalendarItemField[] = []
  const push = (
    label: string,
    id: string | undefined,
    value: string | undefined,
    mark: CalendarFieldMark
  ) => {
    if (id && value) fields.push({ id, label, mark, value })
  }

  push(
    'Story',
    item.storyId,
    item.storyId && resolveDisplay?.('story', item.storyId),
    'story'
  )
  push(
    'Moment',
    item.parentId,
    item.parentId &&
      (itemsById.get(item.parentId)?.title ??
        resolveDisplay?.('moment', item.parentId)),
    'moment'
  )
  push(
    'Channel',
    item.channelId,
    item.channelId && resolveDisplay?.('channel', item.channelId),
    'channel'
  )
  push(
    'Account',
    item.accountId,
    item.accountId && resolveDisplay?.('account', item.accountId),
    'account'
  )
  push(
    'Workflow',
    item.statusId,
    item.statusId && resolveDisplay?.('status', item.statusId),
    'workflow'
  )

  return fields
}

/** The labels an item carries from one set, named, in the item's own order. */
export type CalendarItemLabelSet = {
  /** The set's name, which is the field's label. */
  label: string
  values: ReadonlyArray<{ id: string; name: string }>
}

/** The set a label is filed under when its own set cannot be named. */
const UNSORTED_LABELS = 'Labels'

/**
 * The item's labels, grouped by the set each belongs to, in the order the sets
 * first appear on the item.
 *
 * A set is a field of its own, so the panel draws labels the way it draws any
 * multi-select field. A label the resolver cannot name is left out, as any
 * relationship it cannot name is; a label whose set it cannot name still shows,
 * under a plain "Labels" heading, because the label itself is a fact about the
 * item.
 */
export const buildItemLabelSets = ({
  item,
  resolveDisplay,
}: Pick<
  BuildItemFieldsParams,
  'item' | 'resolveDisplay'
>): CalendarItemLabelSet[] => {
  const sets = new Map<string, { id: string; name: string }[]>()

  item.labelIds?.forEach((id) => {
    const name = resolveDisplay?.('label', id)
    if (name === undefined) return

    const set = resolveDisplay?.('labelSet', id) ?? UNSORTED_LABELS
    sets.set(set, [...(sets.get(set) ?? []), { id, name }])
  })

  return Array.from(sets, ([label, values]) => ({ label, values }))
}
